import React, { useEffect, useMemo } from 'react';
import { useWorldStore } from '../../state/worldStore';
import { useCityStore } from '../../city/cityStore';
import { setGeo } from '../../city/cityMotion';
import { useVenueAnchors } from '../../hooks/useVenueAnchors';
import CityResident from './CityResident';
import StageBand from './StageBand';

// CityResidents — everyone who lives in the city, in their own bodies.
//
// The bodies come from the same place the street always got them (NpcManager:
// roster.json plus the server's built models, `npcList`); WHO is here and WHERE
// comes from the city's stage manager (city/cityStore). A resident with no body
// loaded yet stands in as a silhouette until one arrives.
//
// ⚠ ONE ARTIST, ONE PLACE. Khari's rule from the plan: an artist out in the city
// is not also standing in a room. Everyone in `byId` is drawn here and nowhere
// else on the street (NpcManager stands down while the city is live).
export default function CityResidents() {
  const live = useCityStore((s) => s.live);
  const snap = useCityStore((s) => s.snap);
  const residents = useCityStore((s) => s.snap?.residents || null);
  const npcList = useWorldStore((s) => s.npcList);
  const anchors = useVenueAnchors();

  // The geometry the motion is computed against: the places and the live anchors.
  useEffect(() => { setGeo(snap, anchors); }, [snap, anchors]);

  const bodies = useMemo(() => {
    const m = new Map();
    for (const n of npcList || []) if (n?.avatarId != null && n.rigged && !n.test) m.set(Number(n.avatarId), n);
    return m;
  }, [npcList]);

  // Band members behind the lead live nowhere in the city: drawn on the stage only (StageBand).
  const residentIds = useMemo(() => new Set((residents || []).map((r) => Number(r.avatarId))), [residents]);

  // A PILOT AT HOME (API city/cityHome, 2026-11-05): a cast member who lives on
  // their mark by their pad all day is drawn there by the heliport (heli/
  // HeliPassengers: standing, boarding, flying), never by the street as well.
  // (Their closed hours are theirs too: away, out of the game.)
  const street = useMemo(() => (residents || []).filter((r) => !(r.segs?.some((s) => s.hm === 'heli') && r.segs.every((s) => s.k === 'away' || s.hm === 'heli'))), [residents]);

  if (!live || !residents?.length) return null;
  return (
    <group>
      {street.map((r) => (
        <CityResident key={r.avatarId} resident={r} body={bodies.get(Number(r.avatarId)) || null} />
      ))}
      <StageBand bodies={bodies} residentIds={residentIds} />
    </group>
  );
}
